import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Flex, Layout, theme } from 'antd';
import { clientConfig } from 'pos-client/clientConfig.ts';
import { RefundBanner } from 'pos-client/components/orders/Jobs/Refunds/RefundBanner.tsx';
import { useNavMenuContext } from 'pos-client/contexts/NavMenuContext.tsx';
import { useIsMobile } from 'pos-client/hooks/useIsMobile.ts';
import {
  PosPageHeader,
  type PosPageHeaderProps,
} from 'pos-client/layouts/page/PosPageHeader.tsx';
import { isPosPageHeader } from 'pos-client/utils/layoutUtils.ts';

import { AppToolbar } from './AppToolbar.tsx';

export interface AppHeaderProps {
  header?: PosPageHeaderProps | ReactNode;
  headerHeight?: number;
  setHeaderHeight?: (val: number) => void;
  stickyHeader?: boolean;
  stickyTabs?: boolean;
  stickyToolbar?: boolean;
  hideToolbar?: boolean;
  style?: CSSProperties;
  loading?: boolean;
}

export const AppHeader = ({
  header,
  stickyHeader = true,
  stickyToolbar = true,
  style,
  loading = false,
  hideToolbar = false,
}: AppHeaderProps) => {
  const { setHeaderHeight } = useNavMenuContext();
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const stickyHeaderRef = useRef<HTMLElement>(null);
  const stickyRefundBannerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    const update = () => {
      const sum = sectionRefs.current.reduce(
        (acc, el) => acc + (el?.getBoundingClientRect().height || 0),
        0
      );
      setHeaderHeight(sum);
    };

    update(); // Initial measurement

    const observers = sectionRefs.current
      .filter((el): el is HTMLElement => el != null)
      .map((el) => {
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return ro;
      });

    return () => {
      observers.forEach((ro) => ro.disconnect());
    };
  }, [sectionRefs, setHeaderHeight]);

  const pageHeaderProps: PosPageHeaderProps = useMemo(
    () =>
      isPosPageHeader(header)
        ? {
            ...header,
            tabBar: undefined,
            loading: loading,
            style: header.tabBar
              ? {
                  paddingBottom: 0,
                }
              : undefined,
          }
        : {},
    [header, loading]
  );

  const pageHeader = useMemo(
    () =>
      isPosPageHeader(header) ? (
        <PosPageHeader
          ref={(el) => (sectionRefs.current[1] = el)}
          {...pageHeaderProps}
        />
      ) : (
        <PosPageHeader ref={(el) => (sectionRefs.current[1] = el)}>
          {header}
        </PosPageHeader>
      ),
    [header, pageHeaderProps]
  );

  const defaultHeaderStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'sticky',
    padding: 0,
    height: 'fit-content',
    lineHeight: 1,
    zIndex: 100,
  };

  return (
    <>
      {!stickyToolbar && !hideToolbar && (
        <AppToolbar
          ref={(el) => (sectionRefs.current[0] = el)}
          isMobile={isMobile}
          loading={loading}
        />
      )}
      {clientConfig.RefundsFeatureEnabled && (
        <RefundBanner ref={stickyRefundBannerRef} />
      )}
      <Layout.Header
        style={{
          ...defaultHeaderStyle,
          ...style,
          top: stickyRefundBannerRef.current
            ? stickyRefundBannerRef.current?.clientHeight
            : 0,
        }}
        ref={stickyHeaderRef}
      >
        {stickyToolbar && !hideToolbar && (
          <AppToolbar
            ref={(el) => (sectionRefs.current[0] = el)}
            isMobile={isMobile}
            loading={loading}
          />
        )}
        {header && stickyHeader && pageHeader}
      </Layout.Header>
      {header && !stickyHeader && pageHeader}
      {isPosPageHeader(header) && header.tabBar && (
        <Flex
          ref={(el) => (sectionRefs.current[2] = el)}
          style={{
            paddingLeft: token.paddingLG,
            paddingRight: token.paddingLG,
            paddingTop: token.paddingXXS,
            width: '100%',
            top: stickyRefundBannerRef.current
              ? stickyRefundBannerRef.current?.clientHeight +
                (stickyHeaderRef.current?.clientHeight || 0)
              : stickyHeaderRef.current?.clientHeight,
            position: 'sticky',
            zIndex: 100,
            background: token.Layout?.bodyBg,
          }}
        >
          {header.tabBar}
        </Flex>
      )}
    </>
  );
};
